/* Small shared pieces: number fields, steppers, menus, dialogs, toasts. */

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { fromMm, toMm, roundTo, unitDecimals, type Unit } from '@gsb/core';
import { Icon, type IconName } from './icons';
import { useStore } from '../store/store';

/* A number input that commits on Enter / blur (never mid-typing). */
export function NumberField(props: {
  value: number; onCommit: (v: number) => void; unit?: Unit | '%' | '°' | 'mm' | ''; decimals?: number;
  min?: number; max?: number; label?: string; ariaLabel?: string; disabled?: boolean; small?: boolean; mmValue?: boolean; id?: string;
}) {
  const { unit = '', mmValue = false } = props;
  const isLen = mmValue && (unit === 'mm' || unit === 'cm' || unit === 'in');
  const dec = props.decimals ?? (isLen ? unitDecimals(unit as Unit) : 0);
  const shown = isLen ? roundTo(fromMm(props.value, unit as Unit), dec) : roundTo(props.value, dec);
  const [text, setText] = useState(String(shown));
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (!editing) setText(String(shown)); }, [shown, editing]);
  const commit = () => {
    setEditing(false);
    const v = parseFloat(text.replace(',', '.'));
    if (!isFinite(v)) { setText(String(shown)); return; }
    let x = isLen ? toMm(v, unit as Unit) : v;
    if (props.min !== undefined) x = Math.max(props.min, x);
    if (props.max !== undefined) x = Math.min(props.max, x);
    if (Math.abs(x - props.value) > 1e-9) props.onCommit(x);
    else setText(String(shown));
  };
  const input = (
    <span className="unit" data-unit={unit === 'in' ? 'in' : unit}>
      <input
        id={props.id}
        className={'in num' + (props.small ? ' in-sm' : '')}
        inputMode="decimal"
        aria-label={props.ariaLabel || props.label}
        disabled={props.disabled}
        value={text}
        onFocus={e => { setEditing(true); e.currentTarget.select(); }}
        onChange={e => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') { setEditing(false); setText(String(shown)); (e.target as HTMLInputElement).blur(); }
          e.stopPropagation();
        }}
      />
    </span>
  );
  if (!props.label) return input;
  return <label className="field"><span className="label">{props.label}</span>{input}</label>;
}

export function Stepper(props: { value: number; onCommit: (v: number) => void; min?: number; max?: number; small?: boolean; label?: string; ariaLabel?: string }) {
  const { value, min = 0, max = 10000 } = props;
  const [text, setText] = useState(String(value));
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (!editing) setText(String(value)); }, [value, editing]);
  const commit = (v: number) => { const x = Math.max(min, Math.min(max, Math.round(v))); if (x !== value) props.onCommit(x); else setText(String(value)); };
  const el = (
    <span className={'stepper' + (props.small ? ' sm' : '')}>
      <button type="button" aria-label="Fewer" onClick={() => commit(value - 1)} disabled={value <= min}><Icon name="minus" /></button>
      <input inputMode="numeric" aria-label={props.ariaLabel || props.label || 'Quantity'} value={text}
        onFocus={e => { setEditing(true); e.currentTarget.select(); }}
        onChange={e => setText(e.target.value.replace(/[^\d]/g, ''))}
        onBlur={() => { setEditing(false); commit(parseInt(text || '0', 10)); }}
        onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); e.stopPropagation(); }} />
      <button type="button" aria-label="More" onClick={() => commit(value + 1)} disabled={value >= max}><Icon name="plus" /></button>
    </span>
  );
  if (!props.label) return el;
  return <label className="field"><span className="label">{props.label}</span>{el}</label>;
}

export function Switch(props: { checked: boolean; onChange: (v: boolean) => void; children?: ReactNode; disabled?: boolean }) {
  return (
    <label className="switch">
      <input type="checkbox" checked={props.checked} disabled={props.disabled} onChange={e => props.onChange(e.target.checked)} />
      <i />
      {props.children}
    </label>
  );
}

export function Check(props: { checked: boolean; indeterminate?: boolean; onChange: (v: boolean) => void; label: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = !!props.indeterminate; }, [props.indeterminate]);
  return (
    <label className="check">
      <input ref={ref} type="checkbox" aria-label={props.label} checked={props.checked} onChange={e => props.onChange(e.target.checked)} />
      <i><Icon name="check" strokeWidth={3.4} /></i>
    </label>
  );
}

export function Seg<T extends string>(props: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; label: string }) {
  return (
    <div className="seg" role="group" aria-label={props.label}>
      {props.options.map(o => (
        <button key={o.value} type="button" aria-pressed={props.value === o.value} onClick={() => props.onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

/* A button that opens a small menu; closes on outside click / Escape. */
export function MenuButton(props: { button: (open: boolean, toggle: () => void) => ReactNode; children: (close: () => void) => ReactNode; align?: 'left' | 'right'; up?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent | TouchEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', off);
    document.addEventListener('touchstart', off);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', off); document.removeEventListener('touchstart', off); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <div className="menu-wrap" ref={ref}>
      {props.button(open, () => setOpen(o => !o))}
      {open && <div className={'menu' + (props.align === 'right' ? ' right' : '') + (props.up ? ' up' : '')} role="menu">{props.children(() => setOpen(false))}</div>}
    </div>
  );
}

export function MenuItem(props: { icon?: IconName; label: string; kbd?: string; onClick: () => void; disabled?: boolean; danger?: boolean }) {
  return (
    <button type="button" role="menuitem" disabled={props.disabled} className={props.danger ? 'danger' : ''} onClick={props.onClick}>
      {props.icon && <Icon name={props.icon} />}<span>{props.label}</span>{props.kbd && <kbd>{props.kbd}</kbd>}
    </button>
  );
}

export function Dialog(props: { title: string; onClose?: () => void; children: ReactNode; wide?: boolean; labelledBy?: string; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const first = el?.querySelector<HTMLElement>('button, [href], input, select, textarea');
    first?.focus();
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && props.onClose) { e.stopPropagation(); props.onClose(); }
      if (e.key === 'Tab' && el) {
        const f = [...el.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), select, textarea')];
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    };
    document.addEventListener('keydown', key, true);
    return () => { document.removeEventListener('keydown', key, true); prev?.focus?.(); };
  }, []);
  return (
    <div className="scrim" onMouseDown={e => { if (e.target === e.currentTarget) props.onClose?.(); }}>
      <div ref={ref} className={'dialog' + (props.wide ? ' wide' : '') + (props.className ? ' ' + props.className : '')} role="dialog" aria-modal="true" aria-label={props.title}>
        {props.onClose && <button className="ibtn x" aria-label="Close" onClick={props.onClose}><Icon name="x" /></button>}
        {props.children}
      </div>
    </div>
  );
}

/** A phone bottom sheet. With `open={false}` it stays in the page, hidden, so what's inside keeps its state. */
export function BottomSheet(props: { onClose: () => void; children: ReactNode; label: string; className?: string; open?: boolean }) {
  const open = props.open !== false;
  useEffect(() => {
    if (!open) return;
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') props.onClose(); };
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  }, [open]);
  return (
    <>
      <div className="bsheet-scrim" hidden={!open} onClick={props.onClose} />
      <div className={'bsheet' + (props.className ? ' ' + props.className : '')} hidden={!open} role="dialog" aria-label={props.label}><div className="grip" />{props.children}</div>
    </>
  );
}

/** Messages. In the workspace they sit over the canvas, clear of the panels and Review & order. */
export function Toasts({ className }: { className?: string }) {
  const toasts = useStore(s => s.toasts);
  return (
    <div className={'toasts' + (className ? ' ' + className : '')} aria-live="polite">
      {toasts.map(t => (
        <div key={t.id} className={'toast' + (t.bad ? ' is-bad' : '')}>
          <Icon name={t.bad ? 'alert' : 'okCircle'} />
          <span>{t.text}</span>
          {t.action && <button onClick={() => { t.action!.run(); useStore.setState(s => ({ toasts: s.toasts.filter(x => x.id !== t.id) })); }}>{t.action.label}</button>}
        </div>
      ))}
    </div>
  );
}

/* The branded wait: a little heat press over the film. */
export function Wait() {
  const busy = useStore(s => s.busy);
  if (!busy) return null;
  return (
    <div className="wait" role="alertdialog" aria-live="assertive" aria-label={busy.title}>
      <div className="wait-card">
        <div className="press" aria-hidden="true"><i className="plate" /><i className="film" /><i className="base" /></div>
        <h2>{busy.title}</h2>
        {busy.detail && <p>{busy.detail}</p>}
        <div className={'bar' + (busy.progress == null ? ' is-indet' : '')}><i style={busy.progress != null ? { width: Math.round(busy.progress * 100) + '%' } : undefined} /></div>
      </div>
    </div>
  );
}

export function Press({ size = 1 }: { size?: number }) {
  return <div className="press" aria-hidden="true" style={{ transform: 'scale(' + size + ')' }}><i className="plate" /><i className="film" /><i className="base" /></div>;
}
